/**
 * ContinuationReceiptEditModal – 領収明細編集 modal (A2 modal_edit equivalent).
 * Free medical table (5 rows), price summary, 閉じる/保存/保険外明細付/印刷.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppButton,
  AppInputNumber,
  AppModal,
  AppSelect,
  AppTable,
  AppTextField,
} from '@/components/atoms';
import type {
  ContinuationListRow,
  FreeMedicalItem,
} from '@/services/main/continuation/continuationService';
import type { ColumnsType } from 'antd/es/table';
import {
  fetchContinuationEdit,
  saveContinuation,
  printContinuationReceiptPdf,
} from '@/services/main/continuation/continuationService';
import axios from 'axios';
import { getApiErrorMessage } from '@/utils/apiError';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { registerModalForBack } from '@/hooks/useModalBackHandler';
import { FORM_MESSAGES } from '@/constants/formMessages';

/**
 * Decode an axios error body when the request used `responseType: 'blob'`.
 * Server-side ValidationException returns JSON; axios still wraps it as a Blob.
 */
async function readBlobErrorMessage(err: unknown): Promise<string | null> {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const text = await data.text();
    const json = JSON.parse(text) as {
      message?: string;
      errors?: Record<string, string[]>;
    };
    const firstField = json.errors ? Object.values(json.errors)[0] : undefined;
    return firstField?.[0] ?? json.message ?? null;
  } catch {
    return null;
  }
}

const ROW_COUNT = 5;

/** Columns for summary tables (label + value, no header). */
const SUMMARY_TABLE_COLUMNS: ColumnsType<{ label: string; value: string }> = [
  { dataIndex: 'label', key: 'label', title: '', width: 180 },
  { dataIndex: 'value', key: 'value', title: '', align: 'right' },
];

/** Print settings passed from the 印刷オプション section */
export interface ReceiptPrintSettings {
  showPatientNumber: boolean;
  showDate: boolean;
  dateValue: string;
  showClinicName: boolean;
  showClinicAddress: boolean;
  showManagerName: boolean;
  showManagerAddress: boolean;
  showMassagerName: boolean;
  showMassagerAddress: boolean;
}

export interface ContinuationEditData extends Record<string, unknown> {
  initial_inspection_fee?: number;
  harikyu_price?: number;
  electric_treatment_price?: number;
  treatment_report_price?: number;
  treatment_price?: number;
  madoguchi_price?: number;
  invoice?: number;
  default?: string;
  free_medical_treatment_name1?: string;
  free_medical_treatment_name2?: string;
  free_medical_treatment_name3?: string;
  free_medical_treatment_name4?: string;
  free_medical_treatment_name5?: string;
  free_medical_treatment_count1?: string;
  free_medical_treatment_count2?: string;
  free_medical_treatment_count3?: string;
  free_medical_treatment_count4?: string;
  free_medical_treatment_count5?: string;
  free_medical_treatment_fee1?: string;
  free_medical_treatment_fee2?: string;
  free_medical_treatment_fee3?: string;
  free_medical_treatment_fee4?: string;
  free_medical_treatment_fee5?: string;
}

export interface ContinuationReceiptEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: ContinuationListRow | null;
  freeMedicals: FreeMedicalItem[];
  siteType: string;
  clinicNumber: string;
  onSaved?: () => void;
  printSettings?: ReceiptPrintSettings;
  hideTreatmentFeeRow?: boolean;
}

const DEFAULT_PRINT_SETTINGS: ReceiptPrintSettings = {
  showPatientNumber: false,
  showDate: false,
  dateValue: '',
  showClinicName: false,
  showClinicAddress: false,
  showManagerName: false,
  showManagerAddress: false,
  showMassagerName: false,
  showMassagerAddress: false,
};

const ContinuationReceiptEditModal: React.FC<ContinuationReceiptEditModalProps> = ({
  isOpen,
  onClose,
  record,
  freeMedicals,
  siteType,
  clinicNumber,
  onSaved,
  printSettings = DEFAULT_PRINT_SETTINGS,
  hideTreatmentFeeRow = false,
}) => {
  const toast = useAppToast();
  const appModal = useAppModal();
  const [editData, setEditData] = useState<ContinuationEditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printingType, setPrintingType] = useState<0 | 1 | null>(null);

  const [freeNames, setFreeNames] = useState<Record<number, string>>({
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
  });
  const [freeAmounts, setFreeAmounts] = useState<Record<number, number>>({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });
  const [selectIds, setSelectIds] = useState<Record<number, number>>({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });
  const [initialFormData, setInitialFormData] = useState<{
    names: Record<number, string>;
    amounts: Record<number, number>;
  } | null>(null);

  const formData = useMemo(
    () => ({ names: freeNames, amounts: freeAmounts }),
    [freeNames, freeAmounts]
  );

  const { hasFormChanged, confirmIfChanged, clearFormChange } = useFormChangeGuard(
    formData,
    initialFormData
  );

  const visitId = record?.id ?? 0;
  const isReportOnly = !!record?.is_report_only;
  const [printedInSession, setPrintedInSession] = useState(false);
  const hasPrinted = !!record?.print_invoice_mark || printedInSession;
  const freeMedicalTotal = (Object.values(freeAmounts) as number[]).reduce((a, b) => a + b, 0);
  // ①窓口負担額 — A2 parity: use BE-returned madoguchi_price as-is, do NOT derive
  // from print_invoice − free total. A2 does not recalculate this field when the
  // user edits 自由診療 rows in the table below.
  const madoguchiPrice = editData?.madoguchi_price ?? 0;
  const totalPayment = madoguchiPrice + freeMedicalTotal;
  // Until the receipt is actually printed, ③ mirrors ④ so the diff stays at 0
  // even while the user is editing free-medical rows. After printing, BE persists
  // print_invoice and we use editData.invoice as the source of truth for ③.
  const invoice = hasPrinted ? (editData?.invoice ?? totalPayment) : totalPayment;
  const diffPrice = invoice - totalPayment;

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || !visitId) return;
    let cancelled = false;
    setLoading(true);
    setEditData(null);
    setPrintedInSession(false);
    setInitialFormData(null);
    fetchContinuationEdit(siteType, clinicNumber, visitId)
      .then((data: ContinuationEditData) => {
        if (cancelled) return;
        setEditData(data);
        const names: Record<number, string> = { 1: '', 2: '', 3: '', 4: '', 5: '' };
        const amounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        const ids: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        for (let i = 1; i <= ROW_COUNT; i++) {
          const name = (data[`free_medical_treatment_name${i}`] as string) ?? '';
          const fee = (data[`free_medical_treatment_fee${i}`] as string) ?? '0';
          names[i] = name;
          amounts[i] = parseInt(fee, 10) || 0;
        }
        setFreeNames(names);
        setFreeAmounts(amounts);
        setSelectIds(ids);
        setInitialFormData({ names, amounts });
      })
      .catch(err => {
        if (!cancelled) {
          toastRef.current.error(getApiErrorMessage(err, 'データの取得に失敗しました。'));
          onCloseRef.current();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Only fetch on modal open or visitId change; intentionally exclude toast/onClose
    // from deps so the API isn't re-called every time the parent re-renders.
  }, [isOpen, visitId, siteType, clinicNumber]);

  const setFreeName = useCallback((row: number, value: string) => {
    setFreeNames(prev => ({ ...prev, [row]: value }));
  }, []);
  const setFreeAmount = useCallback((row: number, value: number) => {
    setFreeAmounts(prev => ({ ...prev, [row]: value }));
  }, []);
  const setSelectId = useCallback(
    (row: number, id: number) => {
      setSelectIds(prev => ({ ...prev, [row]: id }));
      const fm = freeMedicals.find(f => f.id === id);
      if (fm) {
        setFreeNames(prev => ({ ...prev, [row]: fm.name }));
        setFreeAmounts(prev => ({ ...prev, [row]: fm.price }));
      } else {
        setFreeNames(prev => ({ ...prev, [row]: '' }));
        setFreeAmounts(prev => ({ ...prev, [row]: 0 }));
      }
    },
    [freeMedicals]
  );

  const handleClearRow = useCallback((row: number) => {
    setFreeNames(prev => ({ ...prev, [row]: '' }));
    setFreeAmounts(prev => ({ ...prev, [row]: 0 }));
    setSelectIds(prev => ({ ...prev, [row]: 0 }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!visitId) return;
    for (let i = 1; i <= ROW_COUNT; i++) {
      if (freeAmounts[i] > 0 && !freeNames[i]?.trim()) {
        toast.warning('自由診療・物販を入力してください。');
        return;
      }
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        visit_id: visitId,
        free_count1: '1',
        free_count2: '1',
        free_count3: '1',
        free_count4: '1',
        free_count5: '1',
      };
      for (let i = 1; i <= ROW_COUNT; i++) {
        payload[`free_name${i}`] = freeNames[i] ?? '';
        payload[`free_amount${i}`] = String(freeAmounts[i] ?? 0);
      }
      await saveContinuation(siteType, clinicNumber, payload);
      toast.success('保存しました。');
      onSaved?.();
      clearFormChange();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, '保存に失敗しました。'));
    } finally {
      setSaving(false);
    }
  }, [
    visitId,
    siteType,
    clinicNumber,
    freeNames,
    freeAmounts,
    totalPayment,
    toast,
    onSaved,
    onClose,
    clearFormChange,
  ]);

  const handlePrint = useCallback(
    async (isFreeMedical: number) => {
      if (!visitId) return;
      for (let i = 1; i <= ROW_COUNT; i++) {
        if (freeAmounts[i] > 0 && !freeNames[i]?.trim()) {
          toast.warning('自由診療・物販を入力してください。');
          return;
        }
      }
      setPrintingType(isFreeMedical === 1 ? 1 : 0);
      try {
        const payload: Record<string, unknown> = {
          visit_id: visitId,
          rezept_id: record?.rezept_id,
          is_free_medical: isFreeMedical,
          print_invoice_save: String(totalPayment),
          show_patient_number: printSettings.showPatientNumber ? 1 : 0,
          show_date: printSettings.showDate ? 1 : 0,
          date_value: printSettings.dateValue || '',
          show_clinic_name: printSettings.showClinicName ? 1 : 0,
          show_clinic_address: printSettings.showClinicAddress ? 1 : 0,
          show_manager_name: printSettings.showManagerName ? 1 : 0,
          show_manager_address: printSettings.showManagerAddress ? 1 : 0,
          show_massager_name: printSettings.showMassagerName ? 1 : 0,
          show_massager_address: printSettings.showMassagerAddress ? 1 : 0,
        };
        for (let i = 1; i <= ROW_COUNT; i++) {
          payload[`free_name${i}`] = freeNames[i] ?? '';
          payload[`free_amount${i}`] = String(freeAmounts[i] ?? 0);
        }
        const blob = await printContinuationReceiptPdf(siteType, clinicNumber, payload);
        const url = URL.createObjectURL(
          new File([blob], 'receipt.pdf', { type: 'application/pdf' })
        );
        window.open(url, '_blank');
        toast.success('印刷を実行しました。');
        setEditData(prev => (prev ? { ...prev, invoice: totalPayment } : null));
        setPrintedInSession(true);
        setInitialFormData({ names: { ...freeNames }, amounts: { ...freeAmounts } });
        onSaved?.();
      } catch (err) {
        const blobMessage = await readBlobErrorMessage(err);
        if (blobMessage) {
          appModal.error({ title: blobMessage, okText: 'OK' });
        } else {
          toast.error(getApiErrorMessage(err, '印刷に失敗しました。'));
        }
      } finally {
        setPrintingType(null);
      }
    },
    [
      visitId,
      record?.rezept_id,
      siteType,
      clinicNumber,
      freeNames,
      freeAmounts,
      totalPayment,
      toast,
      appModal,
      onSaved,
    ]
  );

  const handleClose = useCallback(() => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  }, [confirmIfChanged, clearFormChange, onClose]);

  const handleBrowserBack = useCallback(() => {
    if (hasFormChanged) {
      if (window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION)) {
        clearFormChange();
        onClose();
      }
      return;
    }
    onClose();
  }, [hasFormChanged, clearFormChange, onClose]);

  const handleBrowserBackRef = useRef(handleBrowserBack);
  handleBrowserBackRef.current = handleBrowserBack;

  useEffect(() => {
    if (!isOpen) return;
    let unregister: (() => void) | null = null;
    const doRegister = () => {
      unregister = registerModalForBack(() => {
        handleBrowserBackRef.current();
        doRegister();
      });
    };
    doRegister();
    return () => unregister?.();
  }, [isOpen]);

  if (!record) return null;

  return (
    <>
      <AppModal
        isOpen={isOpen}
        onClose={handleClose}
        title="領収明細編集"
        loading={loading}
        maxWidth={980}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-gray-200">
            <div className="flex gap-2">
              <AppButton
                type="primary"
                loading={printingType === 0}
                disabled={printingType === 1}
                onClick={() => handlePrint(0)}
              >
                印刷
              </AppButton>
              <AppButton
                type="primary"
                loading={printingType === 1}
                disabled={isReportOnly || printingType === 0}
                onClick={() => handlePrint(1)}
              >
                保険外明細付
              </AppButton>
              <AppButton
                type="primary"
                loading={saving}
                onClick={isReportOnly ? () => {} : handleSave}
              >
                保存
              </AppButton>
            </div>
          </div>
        }
      >
        {!loading && editData && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
              {/* Left: 自由診療・物販 (60%) */}
              <div className="min-w-0 overflow-hidden" style={{ maxWidth: 540 }}>
                <AppTable<{ key: number; row: number }>
                  className="continuation-free-medical-table"
                  rowKey="key"
                  pagination={false}
                  scroll={undefined}
                  dataSource={[1, 2, 3, 4, 5].map(row => ({ key: row, row }))}
                  columns={
                    [
                      {
                        title: '自由診療・物販',
                        key: 'free_medical',
                        children: [
                          {
                            title: '',
                            key: 'select',
                            width: 160,
                            render: (_: unknown, { row }: { row: number }) => (
                              <AppSelect
                                className="!h-8"
                                placeholder=""
                                style={{ width: 140 }}
                                value={selectIds[row] || undefined}
                                onChange={v => setSelectId(row, (v as number) ?? 0)}
                                options={[
                                  { value: 0, label: '' },
                                  ...freeMedicals.map(f => ({ value: f.id, label: f.name })),
                                ]}
                                allowClear
                                disabled={isReportOnly}
                              />
                            ),
                          },
                          {
                            title: '',
                            key: 'name',
                            width: 180,
                            render: (_: unknown, { row }: { row: number }) => (
                              <AppTextField
                                style={{ width: 160 }}
                                value={freeNames[row]}
                                onChange={e => setFreeName(row, e.target.value)}
                                placeholder=""
                                disabled={isReportOnly}
                              />
                            ),
                          },
                        ],
                      },
                      {
                        title: '金額(税込み)',
                        key: 'amount',
                        width: 100,
                        align: 'right',
                        render: (_: unknown, { row }: { row: number }) => (
                          <span className="inline-flex items-center gap-1">
                            <AppInputNumber
                              min={0}
                              step={1}
                              className="!h-8 w-20 text-right"
                              value={freeAmounts[row] ?? undefined}
                              parser={val => {
                                const n = parseInt(String(val), 10);
                                return isNaN(n) || n < 1 ? 0 : n;
                              }}
                              onChange={v => {
                                const raw = typeof v === 'number' ? v : Number(v) || 0;
                                setFreeAmount(row, raw < 1 ? 0 : Math.trunc(raw));
                              }}
                              disabled={isReportOnly}
                            />
                            円
                          </span>
                        ),
                      },
                      {
                        title: '',
                        key: 'action',
                        width: 100,
                        render: (_: unknown, { row }: { row: number }) => (
                          <AppButton
                            type="primary"
                            size="small"
                            onClick={() => handleClearRow(row)}
                            disabled={isReportOnly}
                          >
                            入力クリア
                          </AppButton>
                        ),
                      },
                    ] as ColumnsType<{ key: number; row: number }>
                  }
                />
              </div>

              {/* Right: Summary */}
              <div className="space-y-2 continuation-summary-tables">
                <AppTable<{ label: string; value: string }>
                  className="continuation-summary-table"
                  rowKey="label"
                  pagination={false}
                  showHeader={false}
                  dataSource={[
                    {
                      label: '初検料',
                      value: `${(editData.initial_inspection_fee ?? 0).toLocaleString()}円`,
                    },
                    ...(hideTreatmentFeeRow
                      ? []
                      : [
                          {
                            label: '施術料',
                            value: `${(editData.harikyu_price ?? 0).toLocaleString()}円`,
                          },
                        ]),
                    {
                      label: '電気鍼・電気温灸 等',
                      value: `${(editData.electric_treatment_price ?? 0).toLocaleString()}円`,
                    },
                    {
                      label: '施術報告書交付料',
                      value: `${(editData.treatment_report_price ?? 0).toLocaleString()}円`,
                    },
                    {
                      label: '合計金額',
                      value: `${(editData.treatment_price ?? 0).toLocaleString()}円`,
                    },
                    { label: '①窓口負担額', value: `${madoguchiPrice.toLocaleString()}円` },
                  ]}
                  columns={SUMMARY_TABLE_COLUMNS}
                />
                <AppTable<{ label: string; value: string }>
                  className="continuation-summary-table"
                  rowKey="label"
                  pagination={false}
                  showHeader={false}
                  dataSource={[
                    { label: '②自由診療合計', value: `${freeMedicalTotal.toLocaleString()}円` },
                  ]}
                  columns={SUMMARY_TABLE_COLUMNS}
                />
                <AppTable<{ label: string; value: string }>
                  className="continuation-summary-table"
                  rowKey="label"
                  pagination={false}
                  showHeader={false}
                  dataSource={[{ label: '③領収金額', value: `${invoice.toLocaleString()}円` }]}
                  columns={SUMMARY_TABLE_COLUMNS}
                />
                <AppTable<{ label: string; value: string }>
                  className="continuation-summary-table"
                  rowKey="label"
                  pagination={false}
                  showHeader={false}
                  dataSource={[
                    { label: '④支払金額(①+②)', value: `${totalPayment.toLocaleString()}円` },
                    {
                      label: '⑤差異(③-④)',
                      value: `${diffPrice.toLocaleString()}円`,
                    },
                  ]}
                  columns={SUMMARY_TABLE_COLUMNS}
                />
              </div>
            </div>
          </div>
        )}
      </AppModal>
    </>
  );
};

export default ContinuationReceiptEditModal;
